import React, { useState, useEffect, Suspense, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import { SETTINGS_TAB_IDS, settingsTabIndexFromSearch, settingsTabIdFromIndex } from '../../utils/settingsTabs';
import PageHeader from '../../components/ui/PageHeader';
import ProfileTab from './tabs/ProfileTab';
import PaymentMethodTab from './tabs/PaymentMethodTab';
import SecurityTab from './tabs/SecurityTab';
import NotificationsTab from './tabs/NotificationsTab';
import SubscriptionTab from './tabs/SubscriptionTab';

const OrganizationSettings = React.lazy(() => import('./OrganizationSettings'));
const TeamSettings = React.lazy(() => import('../Operations/OperationsTeamPage'));

const NAV_GROUPS = [
  {
    title: 'Account',
    items: [
      { id: 'profile', label: 'Profile' },
      { id: 'security', label: 'Security' },
      { id: 'notifications', label: 'Notifications' },
    ],
  },
  {
    title: 'Organization',
    items: [
      { id: 'branding', label: 'Branding' },
      { id: 'team', label: 'Team' },
    ],
  },
  {
    title: 'Billing',
    items: [
      { id: 'subscription', label: 'Subscription' },
      { id: 'payment', label: 'Payment method' },
    ],
  },
];

const useStyles = makeStyles((theme) => ({
  shell: {
    marginLeft: 'var(--app-shell-offset, 92px)',
    marginTop: 88,
    padding: theme.spacing(2, 3, 6),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
    },
  },
  layout: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--space-8)',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: 'var(--space-4)',
    },
  },
  nav: {
    width: 220,
    flexShrink: 0,
    position: 'sticky',
    top: 88,
    [theme.breakpoints.down('sm')]: {
      position: 'static',
      width: '100%',
      display: 'flex',
      overflowX: 'auto',
      gap: 8,
    },
  },
  navGroup: {
    marginBottom: 'var(--space-5)',
    [theme.breakpoints.down('sm')]: {
      marginBottom: 0,
      display: 'flex',
      gap: 4,
      flexShrink: 0,
    },
  },
  navGroupTitle: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--text-tertiary)',
    margin: '0 0 8px 10px',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  navItem: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    border: 'none',
    background: 'transparent',
    borderRadius: 'var(--radius-sm)',
    padding: '9px 10px',
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    transition: 'background var(--transition), color var(--transition)',
    whiteSpace: 'nowrap',
    '&:hover': {
      background: 'var(--gray-100)',
      color: 'var(--text)',
    },
  },
  navItemActive: {
    background: 'var(--brand-soft)',
    color: 'var(--brand)',
    fontWeight: 600,
    '&:hover': {
      background: 'var(--brand-soft)',
      color: 'var(--brand)',
    },
  },
  content: {
    flex: 1,
    minWidth: 0,
    maxWidth: 720,
  },
}));

const Settings = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [tab, setTab] = useState(() => settingsTabIndexFromSearch(location.search));

  useEffect(() => {
    const idx = settingsTabIndexFromSearch(location.search);
    setTab(idx);
  }, [location.search]);

  const goToTab = useCallback(
    (id) => {
      const idx = SETTINGS_TAB_IDS.indexOf(id);
      setTab(idx >= 0 ? idx : 0);
      history.replace({ pathname: '/settings-profile', search: `?tab=${id}` });
    },
    [history]
  );

  const activeId = settingsTabIdFromIndex(tab);

  const PANELS = {
    profile: <ProfileTab />,
    payment: <PaymentMethodTab />,
    security: <SecurityTab />,
    notifications: <NotificationsTab />,
    subscription: <SubscriptionTab />,
    branding: (
      <Suspense fallback={<Typography>Loading branding…</Typography>}>
        <OrganizationSettings embedMode />
      </Suspense>
    ),
    team: (
      <Suspense fallback={<Typography>Loading team settings…</Typography>}>
        <TeamSettings embedMode />
      </Suspense>
    ),
  };

  const PAGE_META = {
    profile: { title: 'Profile', description: 'Your personal account details.' },
    payment: { title: 'Payment method', description: 'Manage the card on file for billing.' },
    security: { title: 'Security', description: 'Password and account security settings.' },
    notifications: { title: 'Notifications', description: 'Choose what you get notified about.' },
    subscription: { title: 'Subscription', description: 'Manage your plan and billing.' },
    branding: { title: 'Branding', description: 'Organization details and brand colors.' },
    team: { title: 'Team', description: 'Invite and manage teammates.' },
  };

  return (
    <div className="content-body">
      <div className={classes.shell}>
        <div className={classes.layout}>
          <nav className={classes.nav} aria-label="Settings">
            {NAV_GROUPS.map((group) => (
              <div className={classes.navGroup} key={group.title}>
                <div className={classes.navGroupTitle}>{group.title}</div>
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`${classes.navItem} ${activeId === item.id ? classes.navItemActive : ''}`}
                    onClick={() => goToTab(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ))}
          </nav>
          <div className={classes.content}>
            <PageHeader title={PAGE_META[activeId]?.title} description={PAGE_META[activeId]?.description} />
            {PANELS[activeId]}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
